/**
 * demoData.js — Centralised mock data for BrightStars Incorp. demo customer.
 *
 * Used across Orders, Invoices, Cases, and anywhere else demo data
 * is needed for testing the portal pages. Real API services import
 * from here when VITE_USE_MOCK_API is true (or when the stub runs).
 *
 * Customer: BrightStars Incorp.
 * Sales Area: SD-Canada
 * Sales Entity: SD-Canada
 *
 * GROUP G UPDATE (2026-05-25):
 *   - Replaced 8 synthetic SKUs with 11 real product codes from
 *     products-tophammer.json / products-dth.json
 *   - Added commerce fields: bundledQty, shippingCost, weightKg, weightLb,
 *     estimatedDeliveryDays, availability, productLine, category
 *   - Added order-level: orderedBy, invoiceNumber, deliveryMethod,
 *     orderCriticality, subtotal, shippingHandling, taxes
 *   - Added per-line: status, adjustments, tax
 *
 * @module data/demoData
 * @since 2026-05-25 (Group F portal demo data, Group G commerce enrichment)
 */

// ---------------------------------------------------------------------------
// Customer
// ---------------------------------------------------------------------------

export const DEMO_CUSTOMER = {
  customerId: 'BRST-4210',
  customerName: 'BrightStars Incorp.',
  country: 'Canada',
  salesArea: 'SD-Canada',
  salesEntity: 'SD-Canada',
  shippingAddresses: [
    {
      id: 'addr-1',
      label: '780-Goldmine Blvd, Timmins (Primary)',
      street: '780 Goldmine Blvd',
      city: 'Timmins',
      province: 'ON',
      postalCode: 'P4N 7K3',
      country: 'Canada',
      isDefault: true,
    },
    {
      id: 'addr-2',
      label: '110-Go Drilling c/o Carmeuse Lime, Beachville (For Rock Tools)',
      street: '110 Go Drilling c/o Carmeuse Lime',
      city: 'Beachville',
      province: 'ON',
      postalCode: 'N0J 1A0',
      country: 'Canada',
      isDefault: false,
    },
  ],
  billingAddresses: [
    { id: 'bill-1', label: '70-C/O LES INSTALLATION GEOTHERMIX,VARENNES' },
    { id: 'bill-2', label: '73-CFI MINE M-ROC,ST-LAURENCE NL' },
    { id: 'bill-3', label: '74-C/O CONSTRUCTION GELY,L\'ANCIENNE-LORETTE' },
    { id: 'bill-4', label: '76-C/O CBM — OSPREY QUARRY,SINGHAMPTON' },
  ],
  billingAddress: '70-C/O LES INSTALLATION GEOTHERMIX,VARENNES',
  // Legacy flat address for backward compat
  get shippingAddress() {
    const d = this.shippingAddresses.find(a => a.isDefault) || this.shippingAddresses[0];
    return `${d.street}, ${d.city} ${d.province} ${d.postalCode}`;
  },
  paymentTerms: 'Net 30',
  currency: 'CAD',
};

// ---------------------------------------------------------------------------
// Products (11 real SKUs used in demo orders/invoices/cart)
// ---------------------------------------------------------------------------

export const DEMO_PRODUCTS = [
  // ── Top Hammer (4) ──────────────────────────────────────────────
  {
    itemCode: '7306-4772-01',
    description: 'SHANK ADAPTER T51 HC170 D60 L807',
    unitPrice: 485.00,
    weightKg: 15.19,
    weightLb: 33.42,
    bundledQty: 0,
    shippingCost: 0,
    productLine: 'top-hammer',
    category: 'Shank Adapters',
    availability: 'In stock',
    estimatedDeliveryDays: 5,
  },
  {
    itemCode: '7324-4724C-20',
    description: 'MF ROD T38 R39 L=2435MM',
    unitPrice: 312.00,
    weightKg: 20.09,
    weightLb: 44.20,
    bundledQty: 10,
    shippingCost: 10.00,
    productLine: 'top-hammer',
    category: 'MF Rods',
    availability: 'In stock',
    estimatedDeliveryDays: 7,
  },
  {
    itemCode: '7314-3155',
    description: 'COUPLING SLEEVE T38',
    unitPrice: 78.00,
    weightKg: 2.0,
    weightLb: 4.4,
    bundledQty: 0,
    shippingCost: 0,
    productLine: 'top-hammer',
    category: 'Coupling sleeves',
    availability: 'In stock',
    estimatedDeliveryDays: 5,
  },
  {
    itemCode: '7514-4664A-S81',
    description: 'RETRAC BIT T38 64MM GC81',
    unitPrice: 198.50,
    weightKg: 2.0,
    weightLb: 4.4,
    bundledQty: 6,
    shippingCost: 15.00,
    productLine: 'top-hammer',
    category: 'Retrac Bits',
    availability: 'In stock',
    estimatedDeliveryDays: 7,
  },

  // ── DTH — RH560g 5" rig set (5) ────────────────────────────────
  {
    itemCode: '32-5655-GQA-04C',
    description: 'RH560g 5" HAMMER SET 3 1/2 API PIN',
    unitPrice: 4850.00,
    weightKg: 95.0,
    weightLb: 209.44,
    bundledQty: 0,
    shippingCost: 0,
    productLine: 'dth',
    category: 'DTH Hammers',
    availability: 'In stock',
    estimatedDeliveryDays: 14,
  },
  {
    itemCode: '42-3R13100-S48',
    description: 'BIT 560 3.5 100 CC 2/8 13S/12S',
    unitPrice: 1220.00,
    weightKg: 8.5,
    weightLb: 18.74,
    bundledQty: 0,
    shippingCost: 0,
    productLine: 'dth',
    category: 'DTH Bits',
    availability: 'In stock',
    estimatedDeliveryDays: 10,
  },
  {
    itemCode: '42-3R54105-R48',
    description: 'BIT 560 3.5 105 CV 2/8 13B/13B',
    unitPrice: 1350.00,
    weightKg: 9.2,
    weightLb: 20.28,
    bundledQty: 0,
    shippingCost: 0,
    productLine: 'dth',
    category: 'DTH Bits',
    availability: 'Contact us',
    estimatedDeliveryDays: 14,
  },
  {
    itemCode: 'BG00418976',
    description: 'RH560g - 5" PISTON',
    unitPrice: 680.00,
    weightKg: 12.0,
    weightLb: 26.46,
    bundledQty: 0,
    shippingCost: 0,
    productLine: 'dth',
    category: 'DTH Hammers',
    subcategory: 'Spare Parts',
    availability: 'In stock',
    estimatedDeliveryDays: 10,
  },
  {
    itemCode: '52-076-101017',
    description: 'DP076/2000/4/PB/238AR/4F65',
    unitPrice: 890.00,
    weightKg: 45.0,
    weightLb: 99.21,
    bundledQty: 0,
    shippingCost: 0,
    productLine: 'dth',
    category: 'DTH Drill Pipes',
    availability: 'In stock',
    estimatedDeliveryDays: 14,
  },

  // ── Extra TH consumables for order variety (2) ──────────────────
  {
    itemCode: '7306-4766-01',
    description: 'SHANK ADAPTER T51 HC110/HC112RP D51 L670',
    unitPrice: 510.00,
    weightKg: 9.073,
    weightLb: 20.0,
    bundledQty: 0,
    shippingCost: 0,
    productLine: 'top-hammer',
    category: 'Shank Adapters',
    availability: 'In stock',
    estimatedDeliveryDays: 5,
  },
  {
    itemCode: '7306-4885-02',
    description: 'SHANK ADAPTER T51 HPR2 D51 L916',
    unitPrice: 530.00,
    weightKg: 13.71,
    weightLb: 30.22,
    bundledQty: 0,
    shippingCost: 0,
    productLine: 'top-hammer',
    category: 'Shank Adapters',
    availability: 'In stock',
    estimatedDeliveryDays: 5,
  },
];

/** Helper: look up a demo product by itemCode */
export function findDemoProduct(itemCode) {
  return DEMO_PRODUCTS.find(p => p.itemCode === itemCode) || null;
}

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

export const DEMO_ORDERS = [
  // ── Order 1: Delivered TH order ─────────────────────────────────
  {
    orderId: 'ORD-2026-00187',
    orderNumber: 'P007821',
    status: 'delivered',
    orderDate: '2026-04-02T09:15:00Z',
    deliveryDate: '2026-04-18T14:30:00Z',
    shippingAddress: DEMO_CUSTOMER.shippingAddresses[0],
    customerRef: 'PO-BS-2026-041',
    orderedBy: 'Jean-Marc Beaulieu',
    invoiceNumber: 'INV-2026-00721',
    deliveryMethod: 'Ship Complete Order',
    orderCriticality: 'Normal',
    currency: 'CAD',
    subtotal: 3564.00,
    shippingHandling: 120.00,
    taxes: 463.32,
    totalAmount: 4147.32,
    lines: [
      {
        lineNumber: 1,
        ...DEMO_PRODUCTS[0],  // SHANK ADAPTER T51 HC170
        qty: 2,
        lineTotal: 970.00,
        status: 'delivered',
        adjustments: 0,
        tax: 126.10,
      },
      {
        lineNumber: 2,
        ...DEMO_PRODUCTS[1],  // MF ROD T38 R39
        qty: 6,
        lineTotal: 1872.00,
        status: 'delivered',
        adjustments: 0,
        tax: 243.36,
      },
      {
        lineNumber: 3,
        ...DEMO_PRODUCTS[3],  // RETRAC BIT T38 64MM GC81
        qty: 6,
        lineTotal: 1191.00,
        status: 'delivered',
        adjustments: -15.00,
        tax: 152.88,
      },
      {
        lineNumber: 4,
        ...DEMO_PRODUCTS[2],  // COUPLING SLEEVE T38
        qty: 4,
        lineTotal: 312.00,
        status: 'delivered',
        adjustments: 0,
        tax: 40.56,
      },
    ],
    trackingNumber: 'FEDX-9823-4501-CA',
    carrier: 'FedEx Freight',
  },

  // ── Order 2: In transit DTH order ───────────────────────────────
  {
    orderId: 'ORD-2026-00203',
    orderNumber: 'P007905',
    status: 'in_transit',
    orderDate: '2026-05-10T11:00:00Z',
    estimatedDelivery: '2026-05-28T00:00:00Z',
    shippingAddress: DEMO_CUSTOMER.shippingAddresses[1],
    customerRef: 'PO-BS-2026-058',
    orderedBy: 'Jean-Marc Beaulieu',
    invoiceNumber: 'INV-2026-00798',
    deliveryMethod: 'Ship Complete Order',
    orderCriticality: 'Urgent',
    currency: 'CAD',
    subtotal: 7290.00,
    shippingHandling: 250.00,
    taxes: 980.20,
    totalAmount: 8520.20,
    lines: [
      {
        lineNumber: 1,
        ...DEMO_PRODUCTS[5],  // BIT 560 3.5 100 CC
        qty: 2,
        lineTotal: 2440.00,
        status: 'shipped',
        adjustments: 0,
        tax: 317.20,
      },
      {
        lineNumber: 2,
        ...DEMO_PRODUCTS[4],  // RH560g 5" HAMMER SET
        qty: 1,
        lineTotal: 4850.00,
        status: 'shipped',
        adjustments: 0,
        tax: 630.50,
      },
    ],
    trackingNumber: 'UPSC-7741-8832-CA',
    carrier: 'UPS Canada',
  },

  // ── Order 3: Processing — mixed TH + DTH ───────────────────────
  {
    orderId: 'ORD-2026-00215',
    orderNumber: 'P007963',
    status: 'processing',
    orderDate: '2026-05-20T08:45:00Z',
    estimatedDelivery: null,
    shippingAddress: DEMO_CUSTOMER.shippingAddresses[0],
    customerRef: 'PO-BS-2026-072',
    orderedBy: 'Sophie Chen',
    invoiceNumber: 'INV-2026-00855',
    deliveryMethod: 'Partial Shipment Allowed',
    orderCriticality: 'Normal',
    currency: 'CAD',
    subtotal: 3558.00,
    shippingHandling: 85.00,
    taxes: 473.59,
    totalAmount: 4116.59,
    lines: [
      {
        lineNumber: 1,
        ...DEMO_PRODUCTS[9],  // SHANK ADAPTER T51 HC110/HC112RP
        qty: 1,
        lineTotal: 510.00,
        status: 'processing',
        adjustments: 0,
        tax: 66.30,
      },
      {
        lineNumber: 2,
        ...DEMO_PRODUCTS[1],  // MF ROD T38 R39
        qty: 3,
        lineTotal: 936.00,
        status: 'processing',
        adjustments: 0,
        tax: 121.68,
      },
      {
        lineNumber: 3,
        ...DEMO_PRODUCTS[6],  // BIT 560 3.5 105 CV
        qty: 1,
        lineTotal: 1350.00,
        status: 'awaiting_stock',
        adjustments: 0,
        tax: 175.50,
      },
      {
        lineNumber: 4,
        ...DEMO_PRODUCTS[7],  // RH560g 5" PISTON
        qty: 1,
        lineTotal: 680.00,
        status: 'processing',
        adjustments: 0,
        tax: 88.40,
      },
      {
        lineNumber: 5,
        ...DEMO_PRODUCTS[2],  // COUPLING SLEEVE T38
        qty: 2,
        lineTotal: 156.00,
        status: 'processing',
        adjustments: -10.00,
        tax: 18.98,
      },
    ],
    trackingNumber: null,
    carrier: null,
  },

  // ── Order 4: Delivered — older TH order ─────────────────────────
  {
    orderId: 'ORD-2026-00098',
    orderNumber: 'P007412',
    status: 'delivered',
    orderDate: '2026-01-15T14:20:00Z',
    deliveryDate: '2026-02-01T10:00:00Z',
    shippingAddress: DEMO_CUSTOMER.shippingAddresses[0],
    customerRef: 'PO-BS-2026-009',
    orderedBy: 'Jean-Marc Beaulieu',
    invoiceNumber: 'INV-2026-00412',
    deliveryMethod: 'Ship Complete Order',
    orderCriticality: 'Normal',
    currency: 'CAD',
    subtotal: 2521.00,
    shippingHandling: 65.00,
    taxes: 336.18,
    totalAmount: 2922.18,
    lines: [
      {
        lineNumber: 1,
        ...DEMO_PRODUCTS[3],  // RETRAC BIT T38 64MM GC81
        qty: 12,
        lineTotal: 2382.00,
        status: 'delivered',
        adjustments: 0,
        tax: 309.66,
      },
      {
        lineNumber: 2,
        ...DEMO_PRODUCTS[2],  // COUPLING SLEEVE T38
        qty: 4,
        lineTotal: 312.00,
        status: 'delivered',
        adjustments: -8.00,
        tax: 39.52,
      },
    ],
    trackingNumber: 'FEDX-7712-3304-CA',
    carrier: 'FedEx Freight',
  },

  // ── Order 5: Cancelled ──────────────────────────────────────────
  {
    orderId: 'ORD-2025-00812',
    orderNumber: 'P006955',
    status: 'cancelled',
    orderDate: '2025-11-22T16:30:00Z',
    cancelledDate: '2025-11-24T09:00:00Z',
    cancelReason: 'Customer requested cancellation — duplicate order',
    shippingAddress: DEMO_CUSTOMER.shippingAddresses[0],
    customerRef: 'PO-BS-2025-188',
    orderedBy: 'Jean-Marc Beaulieu',
    invoiceNumber: null,
    deliveryMethod: 'Ship Complete Order',
    orderCriticality: 'Normal',
    currency: 'CAD',
    subtotal: 970.00,
    shippingHandling: 45.00,
    taxes: 131.95,
    totalAmount: 1146.95,
    lines: [
      {
        lineNumber: 1,
        ...DEMO_PRODUCTS[0],  // SHANK ADAPTER T51 HC170
        qty: 2,
        lineTotal: 970.00,
        status: 'cancelled',
        adjustments: 0,
        tax: 126.10,
      },
    ],
    trackingNumber: null,
    carrier: null,
  },

  // ── Order 6: Delivered — full DTH rig set ───────────────────────
  {
    orderId: 'ORD-2026-00142',
    orderNumber: 'P007650',
    status: 'delivered',
    orderDate: '2026-03-05T10:30:00Z',
    deliveryDate: '2026-03-22T16:00:00Z',
    shippingAddress: DEMO_CUSTOMER.shippingAddresses[1],
    customerRef: 'PO-BS-2026-028',
    orderedBy: 'Jean-Marc Beaulieu',
    invoiceNumber: 'INV-2026-00580',
    deliveryMethod: 'Ship Complete Order',
    orderCriticality: 'Urgent',
    currency: 'CAD',
    subtotal: 9810.00,
    shippingHandling: 350.00,
    taxes: 1320.80,
    totalAmount: 11480.80,
    lines: [
      {
        lineNumber: 1,
        ...DEMO_PRODUCTS[4],  // RH560g 5" HAMMER SET
        qty: 1,
        lineTotal: 4850.00,
        status: 'delivered',
        adjustments: 0,
        tax: 630.50,
      },
      {
        lineNumber: 2,
        ...DEMO_PRODUCTS[5],  // BIT 560 3.5 100 CC
        qty: 2,
        lineTotal: 2440.00,
        status: 'delivered',
        adjustments: 0,
        tax: 317.20,
      },
      {
        lineNumber: 3,
        ...DEMO_PRODUCTS[6],  // BIT 560 3.5 105 CV
        qty: 1,
        lineTotal: 1350.00,
        status: 'delivered',
        adjustments: 0,
        tax: 175.50,
      },
      {
        lineNumber: 4,
        ...DEMO_PRODUCTS[8],  // DRILL PIPE
        qty: 1,
        lineTotal: 890.00,
        status: 'delivered',
        adjustments: 0,
        tax: 115.70,
      },
      {
        lineNumber: 5,
        ...DEMO_PRODUCTS[7],  // RH560g PISTON spare
        qty: 1,
        lineTotal: 680.00,
        status: 'delivered',
        adjustments: -20.00,
        tax: 85.80,
      },
    ],
    trackingNumber: 'FEDX-8815-9903-CA',
    carrier: 'FedEx Freight',
  },
];

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

export const DEMO_INVOICES = [
  {
    invoiceId: 'inv-bs-001',
    invoiceNumber: 'INV-2026-00721',
    orderNumber: 'P007821',
    amount: 4147.32,
    currency: 'CAD',
    status: 'paid',
    issueDate: '2026-04-19T00:00:00Z',
    dueDate: '2026-05-19T00:00:00Z',
    paidDate: '2026-05-12T00:00:00Z',
    pdfUrl: '/api/invoices/inv-bs-001/pdf',
    fileName: 'INV-2026-00721.pdf',
  },
  {
    invoiceId: 'inv-bs-002',
    invoiceNumber: 'INV-2026-00798',
    orderNumber: 'P007905',
    amount: 8520.20,
    currency: 'CAD',
    status: 'pending',
    issueDate: '2026-05-11T00:00:00Z',
    dueDate: '2026-06-11T00:00:00Z',
    paidDate: null,
    pdfUrl: '/api/invoices/inv-bs-002/pdf',
    fileName: 'INV-2026-00798.pdf',
  },
  {
    invoiceId: 'inv-bs-003',
    invoiceNumber: 'INV-2026-00412',
    orderNumber: 'P007412',
    amount: 2922.18,
    currency: 'CAD',
    status: 'paid',
    issueDate: '2026-02-02T00:00:00Z',
    dueDate: '2026-03-02T00:00:00Z',
    paidDate: '2026-02-28T00:00:00Z',
    pdfUrl: '/api/invoices/inv-bs-003/pdf',
    fileName: 'INV-2026-00412.pdf',
  },
  {
    invoiceId: 'inv-bs-004',
    invoiceNumber: 'INV-2025-01890',
    orderNumber: 'P006955',
    amount: 1146.95,
    currency: 'CAD',
    status: 'voided',
    issueDate: '2025-11-25T00:00:00Z',
    dueDate: '2025-12-25T00:00:00Z',
    paidDate: null,
    pdfUrl: '/api/invoices/inv-bs-004/pdf',
    fileName: 'INV-2025-01890.pdf',
  },
  {
    invoiceId: 'inv-bs-005',
    invoiceNumber: 'INV-2026-00855',
    orderNumber: 'P007963',
    amount: 4116.59,
    currency: 'CAD',
    status: 'pending',
    issueDate: '2026-05-21T00:00:00Z',
    dueDate: '2026-06-21T00:00:00Z',
    paidDate: null,
    pdfUrl: '/api/invoices/inv-bs-005/pdf',
    fileName: 'INV-2026-00855.pdf',
  },
  {
    invoiceId: 'inv-bs-006',
    invoiceNumber: 'INV-2026-00580',
    orderNumber: 'P007650',
    amount: 11480.80,
    currency: 'CAD',
    status: 'paid',
    issueDate: '2026-03-23T00:00:00Z',
    dueDate: '2026-04-23T00:00:00Z',
    paidDate: '2026-04-18T00:00:00Z',
    pdfUrl: '/api/invoices/inv-bs-006/pdf',
    fileName: 'INV-2026-00580.pdf',
  },
];

// ---------------------------------------------------------------------------
// Cases
// ---------------------------------------------------------------------------

export const DEMO_CASES = [
  {
    caseId: 'cs-bs-001',
    caseNumber: 'CS-20261',
    subject: 'Delivery delay on order P007905 — DTH hammer shipment',
    status: 'in_progress',
    topicId: 'order-issue',
    createdDate: '2026-05-18T08:30:00Z',
    lastUpdated: '2026-05-22T14:00:00Z',
    createdBy: 'Jean-Marc Beaulieu',
    priority: 'high',
  },
  {
    caseId: 'cs-bs-002',
    caseNumber: 'CS-20258',
    subject: 'Product enquiry — T51 shank adapter compatibility with Sandvik HC170',
    status: 'in_progress',
    topicId: 'product-info',
    createdDate: '2026-05-14T11:15:00Z',
    lastUpdated: '2026-05-16T09:45:00Z',
    createdBy: 'Jean-Marc Beaulieu',
    priority: 'medium',
  },
  {
    caseId: 'cs-bs-003',
    caseNumber: 'CS-20245',
    subject: 'Request for availability — RH560 bits 105mm convex',
    status: 'new',
    topicId: 'request-availability',
    createdDate: '2026-05-23T13:00:00Z',
    lastUpdated: '2026-05-23T13:00:00Z',
    createdBy: 'Jean-Marc Beaulieu',
    priority: 'medium',
  },
  {
    caseId: 'cs-bs-004',
    caseNumber: 'CS-20232',
    subject: 'Return request — 2x Coupling sleeve T38 (wrong thread)',
    status: 'closed',
    topicId: 'return-request',
    createdDate: '2026-04-10T16:00:00Z',
    lastUpdated: '2026-04-22T10:30:00Z',
    createdBy: 'Jean-Marc Beaulieu',
    resolution: 'Return approved. Replacement coupling sleeves shipped 22 Apr.',
  },
  {
    caseId: 'cs-bs-005',
    caseNumber: 'CS-20218',
    subject: 'Sellability enquiry — DTH bits for RH560 in Ontario region',
    status: 'closed',
    topicId: 'product-sellability',
    createdDate: '2026-03-05T09:20:00Z',
    lastUpdated: '2026-03-12T15:00:00Z',
    createdBy: 'Jean-Marc Beaulieu',
    resolution: 'Confirmed sellable via SD-Canada entity. Price list updated.',
  },
  {
    caseId: 'cs-bs-006',
    caseNumber: 'CS-20270',
    subject: 'Request to quote — bulk order MF Rods T38 (50 units)',
    status: 'new',
    topicId: 'request-order',
    createdDate: '2026-05-24T10:00:00Z',
    lastUpdated: '2026-05-24T10:00:00Z',
    createdBy: 'Sophie Chen',
    priority: 'high',
  },
  {
    caseId: 'cs-bs-007',
    caseNumber: 'CS-20199',
    subject: 'Technical support — drill string configuration for granite face',
    status: 'closed',
    topicId: 'product-info',
    createdDate: '2026-02-18T14:30:00Z',
    lastUpdated: '2026-02-25T11:00:00Z',
    createdBy: 'Jean-Marc Beaulieu',
    resolution: 'Recommended T51 configuration with PowerCarbide bits. Customer confirmed setup works well.',
  },
];

/**
 * DEMO_CUSTOMER_ACCOUNTS
 *
 * Mock customer accounts keyed by sales-entity code. Used by AccountSwitcher
 * to populate the "Customer" cascading dropdown for employee users.
 *
 * In production, this list will come from SAP Customer Master via the MRT
 * backend API, filtered by the employee's granted sales-entity access.
 *
 * Shape: { [entityCode]: Array<{ id, name, city, country }> }
 */
export const DEMO_CUSTOMER_ACCOUNTS = {
  // Real master-data integration will key on the numeric sandvikEntityId
  // that the AccountSwitcher dispatches (entity.entityCode in the JSON —
  // '4782' for SD-Canada, etc.). Front-end shows only the customer name;
  // the back-end resolves both the sales entity code AND the customer
  // account number from a single customer pick. See
  // MRT-PWA-Analysis/parked-master-data-integration.md.
  //
  // '4782' — Sandvik Mining and Construction Canada (displayName 'SD-Canada')
  '4782': [
    { id: '6011', name: 'Selix', city: 'Timmins', country: 'Canada' },
  ],
  // Legacy 2-letter keys kept for backwards compat during the transition —
  // these will resolve when a caller passes the 2-letter ERP code, but
  // AccountSwitcher currently passes entity.entityCode (numeric) so they
  // are effectively dead until data-shape is unified.
  SD: [
    { id: 'BRST-4210', name: 'BRST-4210 BRIGHTSTARS INCORP.', city: 'Timmins', country: 'Canada' },
    { id: 'NRGD-1100', name: 'NRGD-1100 NORTHERN GOLD MINES LTD', city: 'Val-d\'Or', country: 'Canada' },
    { id: 'MNPL-3050', name: 'MNPL-3050 MAPLE LEAF MINING CO.', city: 'Sudbury', country: 'Canada' },
  ],
  SU: [
    { id: 'RCKY-2200', name: 'RCKY-2200 ROCKY MOUNTAIN DRILLING INC.', city: 'Elko', country: 'USA' },
    { id: 'GLDR-5500', name: 'GLDR-5500 GOLDEN RIDGE RESOURCES', city: 'Reno', country: 'USA' },
  ],
  SE: [
    { id: 'BLTN-8800', name: 'BLTN-8800 BOLIDEN MINERAL AB', city: 'Boliden', country: 'Sweden' },
    { id: 'LKAB-9900', name: 'LKAB-9900 LUOSSAVAARA-KIIRUNAVAARA AB', city: 'Kiruna', country: 'Sweden' },
  ],
  AU: [
    { id: 'KWST-6600', name: 'KWST-6600 KWEST MINING PTY', city: 'Kalgoorlie', country: 'Australia' },
  ],
  CE: [
    { id: 'GMFR-4400', name: 'GMFR-4400 GOLD FIELDS SOUTH DEEP', city: 'Johannesburg', country: 'South Africa' },
  ],
  BR: [
    { id: 'VLGD-7700', name: 'VLGD-7700 VALE GOLD DIVISION', city: 'Itabira', country: 'Brazil' },
  ],
};

// ---------------------------------------------------------------------------
// Line-item status config (used by OrderDetailPage, CartPage)
// ---------------------------------------------------------------------------

export const LINE_STATUS_CONFIG = {
  processing:      { label: 'Processing',      cls: 'bg-electric-blue text-white' },
  awaiting_stock:  { label: 'Awaiting stock',   cls: 'bg-warning text-carbon-black' },
  shipped:         { label: 'Shipped',          cls: 'bg-[#0078C2] text-white' },
  delivered:       { label: 'Delivered',        cls: 'bg-success text-white' },
  cancelled:       { label: 'Cancelled',        cls: 'bg-n3 text-n6' },
};

